import dayjs from 'dayjs'
import { formatCatchBoardDate } from 'src/utils/catchBoardMappers'

/** Calendar-day window for the milder “due soon” card signal. */
export const CATCH_BOARD_FOLLOW_UP_DUE_SOON_DAYS = 7

export const CATCH_BOARD_FOLLOW_UP_STATUS = Object.freeze({
  OVERDUE: 'overdue',
  DUE_SOON: 'due_soon',
  UPCOMING: 'upcoming',
})

/**
 * Calendar-day delta from today to the follow-up date.
 * Negative = past (overdue), 0 = today, positive = future.
 * @param {string|Date|null|undefined} nextFollowUpAt
 * @param {string|Date|dayjs.Dayjs} [now]
 * @returns {number|null}
 */
export function getCatchBoardFollowUpDayDiff(nextFollowUpAt, now = dayjs()) {
  if (!nextFollowUpAt) {
    return null
  }

  const due = dayjs(nextFollowUpAt)
  const nowAt = dayjs(now)
  if (!due.isValid() || !nowAt.isValid()) {
    return null
  }

  return due.startOf('day').diff(nowAt.startOf('day'), 'day')
}

/**
 * Urgency bucket for card styling. Unset / invalid → null (quiet on the card).
 * @param {string|Date|null|undefined} nextFollowUpAt
 * @param {string|Date|dayjs.Dayjs} [now]
 * @param {{ dueSoonDays?: number }} [options]
 * @returns {'overdue'|'due_soon'|'upcoming'|null}
 */
export function getCatchBoardFollowUpStatus(
  nextFollowUpAt,
  now = dayjs(),
  { dueSoonDays = CATCH_BOARD_FOLLOW_UP_DUE_SOON_DAYS } = {},
) {
  const dayDiff = getCatchBoardFollowUpDayDiff(nextFollowUpAt, now)
  if (dayDiff == null) {
    return null
  }

  if (dayDiff < 0) {
    return CATCH_BOARD_FOLLOW_UP_STATUS.OVERDUE
  }

  if (dayDiff <= dueSoonDays) {
    return CATCH_BOARD_FOLLOW_UP_STATUS.DUE_SOON
  }

  return CATCH_BOARD_FOLLOW_UP_STATUS.UPCOMING
}

/**
 * Short card label for next follow-up. Empty when unset.
 * Examples: "3d overdue", "Today", "Tomorrow", "In 3d", "30 Sep".
 * @param {string|Date|null|undefined} nextFollowUpAt
 * @param {string|Date|dayjs.Dayjs} [now]
 * @returns {string}
 */
export function formatCatchBoardFollowUpLabel(nextFollowUpAt, now = dayjs()) {
  const dayDiff = getCatchBoardFollowUpDayDiff(nextFollowUpAt, now)
  if (dayDiff == null) {
    return ''
  }

  if (dayDiff < 0) {
    const daysOverdue = Math.abs(dayDiff)
    return daysOverdue === 1 ? '1d overdue' : `${daysOverdue}d overdue`
  }

  if (dayDiff === 0) {
    return 'Today'
  }

  if (dayDiff === 1) {
    return 'Tomorrow'
  }

  if (dayDiff <= CATCH_BOARD_FOLLOW_UP_DUE_SOON_DAYS) {
    return `In ${dayDiff}d`
  }

  return dayjs(nextFollowUpAt).format('D MMM')
}

/**
 * Card-ready follow-up view-model. Null when there is nothing to show.
 * @param {string|Date|null|undefined} nextFollowUpAt
 * @param {string|Date|dayjs.Dayjs} [now]
 * @returns {{ status: string, label: string, title: string }|null}
 */
export function getCatchBoardFollowUpDisplay(nextFollowUpAt, now = dayjs()) {
  const status = getCatchBoardFollowUpStatus(nextFollowUpAt, now)
  if (!status) {
    return null
  }

  const label = formatCatchBoardFollowUpLabel(nextFollowUpAt, now)
  if (!label) {
    return null
  }

  return {
    status,
    label,
    title: formatCatchBoardDate(nextFollowUpAt, { empty: '' }),
  }
}
